import { useFeedbackSettingsStore } from '../store/feedbackSettingsStore'
import { unlockFeedbackAudio } from './feedbackSoundService'
import type { FuelSoundType } from '../store/fuelNotificationStore'

type Tone = {
  frequency: number
  duration: number
  type?: OscillatorType
  gain?: number
  delay?: number
}

let audioContext: AudioContext | null = null

function ctx(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (!audioContext) {
    const Ctx = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return null
    audioContext = new Ctx()
  }
  return audioContext
}

function beep(ac: AudioContext, tone: Tone, volume: number) {
  const osc = ac.createOscillator()
  const gain = ac.createGain()
  const start = ac.currentTime + (tone.delay ?? 0)
  osc.type = tone.type ?? 'sine'
  osc.frequency.setValueAtTime(tone.frequency, start)
  const peak = Math.min(0.28, Math.max(0.02, (tone.gain ?? 0.16) * volume))
  gain.gain.setValueAtTime(0.0001, start)
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.012)
  gain.gain.exponentialRampToValueAtTime(0.0001, start + Math.max(0.05, tone.duration))
  osc.connect(gain)
  gain.connect(ac.destination)
  osc.start(start)
  osc.stop(start + tone.duration + 0.03)
}

/** Distinct fuel-station motifs — not the generic ERP notification chime. */
const PATTERNS: Record<FuelSoundType, Tone[]> = {
  meter: [
    { frequency: 330, duration: 0.07, type: 'triangle', gain: 0.14 },
    { frequency: 392, duration: 0.11, type: 'triangle', gain: 0.16, delay: 0.08 },
  ],
  receipt: [
    { frequency: 523, duration: 0.08, gain: 0.15 },
    { frequency: 659, duration: 0.1, gain: 0.17, delay: 0.09 },
    { frequency: 784, duration: 0.16, gain: 0.18, delay: 0.2 },
  ],
  payment: [
    { frequency: 784, duration: 0.09, gain: 0.16 },
    { frequency: 587, duration: 0.11, gain: 0.16, delay: 0.1 },
    { frequency: 392, duration: 0.16, type: 'triangle', gain: 0.17, delay: 0.22 },
  ],
  alert: [
    { frequency: 196, duration: 0.14, type: 'square', gain: 0.12 },
    { frequency: 155, duration: 0.18, type: 'square', gain: 0.12, delay: 0.16 },
    { frequency: 196, duration: 0.2, type: 'square', gain: 0.13, delay: 0.36 },
  ],
  approval: [
    { frequency: 880, duration: 0.08, type: 'sawtooth', gain: 0.1 },
    { frequency: 880, duration: 0.08, type: 'sawtooth', gain: 0.1, delay: 0.14 },
    { frequency: 1175, duration: 0.16, type: 'sawtooth', gain: 0.12, delay: 0.28 },
  ],
}

export function playFuelStationSound(soundType?: string): void {
  const { enabled, volume, categories } = useFeedbackSettingsStore.getState()
  if (!enabled || categories.fuelStation === false) return
  const key = (['meter', 'receipt', 'payment', 'alert', 'approval'].includes(soundType || '')
    ? soundType
    : 'meter') as FuelSoundType
  const ac = ctx()
  if (!ac) return
  if (ac.state === 'suspended') void ac.resume()
  unlockFeedbackAudio()
  PATTERNS[key].forEach((tone) => beep(ac, tone, volume))
}

export function previewFuelStationSound(soundType: FuelSoundType): void {
  unlockFeedbackAudio()
  const previous = useFeedbackSettingsStore.getState().enabled
  useFeedbackSettingsStore.setState({ enabled: true })
  playFuelStationSound(soundType)
  useFeedbackSettingsStore.setState({ enabled: previous })
}
